'use client';

import { Subsidiary } from '@coolblue-development/constants';
import { Box, Flex, freddyTheme } from '@coolblue-development/freddy';
import { useRequestContext } from '@coolblue-development/next-request-context/client';
import { EnergyLabelContainer } from '@features/product/components/OrderBlock/EnergyLabel';
import type { EnergyLabelType, SubProducts } from '@features/product/typings';
import { useBreakpoints } from '@hooks/useBreakpoints';
import { ReactNode } from 'react';

type EnergyLabelRendererProps = {
  energyLabel: EnergyLabelType;
  subProducts: SubProducts[];
  availabilityState: ReactNode;
};

export const EnergyLabelRenderer = ({
  energyLabel,
  subProducts,
  availabilityState
}: EnergyLabelRendererProps) => {
  const hasEnergyLabel =
    energyLabel || subProducts?.some(subProduct => subProduct?.product?.energyLabel);
  const { subsidiary } = useRequestContext();
  const { isMobile } = useBreakpoints();
  const isGermany = subsidiary === Subsidiary.DE;

  if ((!isGermany || !hasEnergyLabel) && isMobile) {
    /* TODO: prevent availability state from blocking the whole page */
    return (
      // This CSS block is temporary, until designers figure out a long term solution.
      // It covers for edge cases where, in very small screens, the availability state collides with the addToCartButton.
      <Box
        sx={{
          'div:has(> svg)': {
            alignItems: 'flex-start'
          },
          p: {
            lineHeight: freddyTheme.typography.lineHeight[400],
            textWrap: 'auto'
          }
        }}
      >
        {availabilityState}
      </Box>
    );
  }

  return hasEnergyLabel ? (
    <Flex justifyContent={{ md: 'flex-end' }}>
      <EnergyLabelContainer
        energyLabel={energyLabel}
        subProducts={subProducts}
        flexDirection={isMobile ? 'column' : 'row'}
        direction={isMobile ? 'right' : 'left'}
        size={isMobile ? 'small' : 'medium'}
      />
    </Flex>
  ) : null;
};
